import { useMutation, useQuery } from '@tanstack/react-query';

import { getAccessToken } from '@/lib/tokenStorage';

import {
  CreateSireRequest,
  CreateSireResult,
  SireDetail,
  UpdateSireRequest,
} from './types';

export type {
  CreateSireRequest,
  CreateSireResult,
  SireDetail,
  UpdateSireRequest,
} from './types';

export const sireQueryKey = (sireId: string) => ['sire', sireId] as const;

function getBaseUrl(): string {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  return API_BASE_URL;
}

function getHeaders(): HeadersInit {
  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export async function fetchSire(sireId: string): Promise<SireDetail> {
  const response = await fetch(`${getBaseUrl()}/api/v1/sire/${sireId}`, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

export async function createSire(
  request: CreateSireRequest,
): Promise<CreateSireResult> {
  const response = await fetch(`${getBaseUrl()}/api/v1/sire`, {
    method: 'POST',
    credentials: 'include',
    headers: getHeaders(),
    body: JSON.stringify(request),
  });

  if (response.status === 409 || response.status === 400) {
    return { success: false, message: await response.text() };
  }

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return { success: true, sire: await response.json() };
}

export async function updateSire(
  sireId: string,
  request: UpdateSireRequest,
): Promise<SireDetail> {
  const response = await fetch(`${getBaseUrl()}/api/v1/sire/${sireId}`, {
    method: 'PUT',
    credentials: 'include',
    headers: getHeaders(),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

export function useSireQuery(sireId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: sireQueryKey(sireId ?? ''),
    queryFn: () => fetchSire(sireId!),
    enabled: enabled && Boolean(sireId),
  });
}

export function useCreateSireMutation() {
  return useMutation({
    mutationFn: (request: CreateSireRequest) => createSire(request),
  });
}

export function useUpdateSireMutation() {
  return useMutation({
    mutationFn: ({
      sireId,
      request,
    }: {
      sireId: string;
      request: UpdateSireRequest;
    }) => updateSire(sireId, request),
    async onSuccess(sire, _variables, _ctx, { client }) {
      client.setQueryData(sireQueryKey(sire.id), sire);
    },
  });
}
